import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import IcSend from "@/assets/icons/ic-send-01.svg";
import IcSendArrow from "@/components/icons/ic-send-arrow";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import type { PublicAgentDetail } from "@/types/agent";
import { createInquiry } from "@/services/inquiry-service";
import icArrowLeft from "@/assets/icons/ic-arrow-left.svg";

const CONTENT_MAX = 500;

const ContactPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [content, setContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const agents: PublicAgentDetail[] = location.state?.agents ?? [];
  const projectId: string | undefined = location.state?.projectId;
  const selectedConsulting: string | undefined = location.state?.selectedConsulting;
  const projectName: string = location.state?.projectName;

  const isValid = content.trim().length > 0;

  const handleReset = () => {
    setContent("");
  };

  const handleSubmit = async () => {
    if (!isValid || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await createInquiry({
        title: projectName,
        content: content.trim(),
        agent_ids: agents.map((a) => a.id),
        project_id: projectId,
        selected_consulting: selectedConsulting,
      });
      toast({ description: "요청이 전송되었습니다." });
      // Delay navigation so the toast renders and is visible before route change.
      // Navigating to "/" immediately swaps the LandingPage and masks the toast.
      // Keep isSubmitting=true during the delay to prevent double-submit.
      setTimeout(() => {
        navigate("/");
        setIsSubmitting(false);
      }, 500);
    } catch {
      toast({
        description: "오류가 발생했습니다. 다시 시도해 주세요.",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isSubmitting && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div 
        className="flex flex-col items-center gap-12 px-5 py-6 pb-10 md:px-8 md:py-10 md:pb-[100px]"
        style={{ display: isSubmitting ? "none" : "flex" }}
      >
      <div className="flex flex-col gap-8 w-full md:gap-12 md:max-w-[608px]">
        {/* Header */}
        <div className="flex flex-col gap-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="w-12 h-12"
            aria-label="뒤로가기"
          >
            <img src={icArrowLeft} alt="Icon Arrow Left" />
          </Button>
          <div className="flex flex-col justify-center items-center gap-3">
            <h1 className="text-[44px] leading-[48px] md:text-[56px] md:leading-[64px] font-normal tracking-[-0.03em] text-[#000000]">
              Contact PwC
            </h1>
            <p className="text-base leading-6 tracking-[-0.01em] text-[#000000]">
              문의사항이 있으시면 아래 양식을 통해 연락주세요
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="flex flex-col gap-6">
          {/* 내용 */}
          <div className="flex flex-col gap-2">
            <div className="flex gap-1">
              <label className="text-sm font-normal leading-5 tracking-[-0.01em] text-[#000000]">
                내용
              </label>
              <span className="text-sm leading-5 text-[#CB3A31]">*</span>
            </div>

            <Textarea
              value={content}
              onChange={(e) => {
                if (e.target.value.length <= CONTENT_MAX) setContent(e.target.value);
              }}
              placeholder="문의 내용을 입력하세요"
              className="min-h-[88px] px-4 py-3 rounded-[4px] bg-white border border-[#00000027] text-base placeholder:text-[#B0B0B0] resize-y"
            />
          </div>

          {/* 선택한 컨설팅 */}
          {selectedConsulting && (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-normal leading-5 tracking-[-0.01em] text-[#000000]">
                선택한 컨설팅
              </label>
              <div className="flex items-center h-12 px-4 py-3 rounded-[4px] bg-white border border-[#00000017]">
                <span className="text-base font-bold leading-6 tracking-[-0.01em] text-[#BBBBBB]">
                  {selectedConsulting ?? "선택된 컨설팅 없음"}
                </span>
              </div>
            </div>
          )}

          {/* 선택한 에이전트 */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-normal leading-5 tracking-[-0.01em] text-[#000000]">
              선택한 에이전트
            </label>
            <div className="flex flex-col gap-2 md:flex-row md:flex-wrap">
              {agents.map((agent) => (
                <div
                  key={agent.id}
                  className="gap-1 p-3 bg-[#EBEBEB] max-w-full overflow-hidden  rounded-[4px]"
                >
                  <span className="text-xs leading-4 text-[#000000] shrink-0 whitespace-pre-line">
                    {agent.provider}{" "}
                  </span>
                  <span className="text-xs font-bold leading-4 text-[#000000] whitespace-pre-line">
                    {agent.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center gap-3 w-full">
          <Button
            variant="ghost"
            onClick={handleReset}
            className="flex-1 h-12 py-3 px-5 bg-black/[0.0627] text-[#202020] text-base font-bold leading-6 tracking-[-0.01em]  rounded-[4px]"
          >
            초기화
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
            className="flex-1 h-12 py-3 pl-5 pr-4 bg-[#FFAA72] text-[#000000] text-base font-bold leading-6 tracking-[-0.01em]  gap-2 rounded-[4px] "
          >
            보내기
            <IcSendArrow fill="currentColor" className="disabled:fill-[#B0B0B0]" />
          </Button>
        </div>
      </div>
    </div>
    </>
  );
};

export default ContactPage;
